import React, { useState, useEffect } from 'react';
import { 
  Box, 
  InputAdornment,
  IconButton,
  Alert
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useVendorAuth } from '../../contexts/VendorAuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { CustomTextField, CustomButton, CustomTypography, validateForm, commonRules } from '../../components/shared';
import { DESIGN_TOKENS } from '../../styles/constants';

// S3 asset URLs
const imgPattern5 = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/auth-bg-pattern.png';
const imgGroup1 = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/auth-illustration.svg';
const logoImg = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/logo.png';

const Login: React.FC = () => {
  usePageTitle('Login');
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const { login, isLoading, vendor } = useVendorAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get return URL from query params
  const returnUrl = searchParams.get('returnUrl') || '/dashboard';

  // Handle redirect after successful login
  useEffect(() => {
    if (shouldRedirect && vendor) {
      // Redirect based on onboarding progress
      if (vendor.onboardingStep < 4) {
        // Onboarding not complete, redirect to appropriate step
        const stepMap: { [key: number]: string } = {
          0: '/onboarding/step1',
          1: '/onboarding/step2', 
          2: '/onboarding/step3',
          3: '/onboarding/step4',
          4: '/onboarding/step5'
        };
        const targetStep = stepMap[vendor.onboardingStep] || '/onboarding/step1';
        navigate(targetStep, { replace: true });
      } else {
        // Onboarding complete, redirect to company profile
        navigate('/company-profile', { replace: true });
      }
      setShouldRedirect(false);
    }
  }, [shouldRedirect, vendor, navigate, returnUrl]);

  const validationRules = {
    email: commonRules.email,
    password: commonRules.required
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: [] }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validate form
    const validation = validateForm(formData, validationRules);
    if (!validation.isValid) {
      const errors: Record<string, string[]> = {};
      validation.errors.forEach(error => {
        const [field, message] = error.split(': ');
        if (!errors[field]) errors[field] = [];
        errors[field].push(message);
      });
      setFieldErrors(errors);
      return;
    }

    try {
      const success = await login(formData.email, formData.password);
      if (success) {
        // Trigger redirect after vendor context updates
        setShouldRedirect(true);
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
        backgroundColor: DESIGN_TOKENS.colors.primary,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Illustration - Hidden on mobile */}
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          width: { xs: 0, md: '66.67%' }, // 2/3 of the page
          height: '100%',
          backgroundImage: `url(${imgPattern5})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box
          component="img"
          src={imgGroup1}
          alt="Illustration"
          sx={{
            width: '752px',
            height: '601px',
            mixBlendMode: 'overlay'
          }}
        />
      </Box>

      {/* Login Form Container */}
      <Box
        sx={{
          width: { xs: '100%', md: '33.33%' }, // 1/3 of the page
          height: '100%',
          backgroundColor: DESIGN_TOKENS.colors.background,
          padding: { xs: '40px 24px', md: '80px' },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '32px', md: '40px' },
          position: 'relative',
          zIndex: 1
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            width: '120px',
            height: '94px',
            position: 'relative'
          }}
        >
          <Box
            component="img"
            src={logoImg}
            alt="Eventi Logo"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '40px',
            justifyContent: 'center'
          }}
        >
          {/* Title Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <CustomTypography variant="h1">
              Sign In
            </CustomTypography>
            <CustomTypography variant="body1">
              Please enter your account credentials to sign in.
            </CustomTypography>
          </Box>

          {/* Form */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {error}
              </Alert>
            )}

            {/* Email Field */}
            <CustomTextField
              fullWidth
              placeholder="Email address"
              value={formData.email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('email', e.target.value)}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email?.[0]}
            />

            {/* Password Field */}
            <CustomTextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={formData.password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('password', e.target.value)}
              error={!!fieldErrors.password}
              helperText={fieldErrors.password?.[0]}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClickShowPassword}
                      edge="end"
                      sx={{ color: DESIGN_TOKENS.colors.text }}
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Sign In Button */}
            <CustomButton
              type="submit"
              fullWidth
              disabled={isLoading}
              variant="primary"
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </CustomButton>
          </Box>

          {/* Forgot Password Button */}
          <CustomButton
            fullWidth
            onClick={handleForgotPassword}
            variant="secondary"
          >
            Forgot password
          </CustomButton>
        </Box>

        {/* Footer */}
        <CustomTypography 
          variant="caption" 
          sx={{ 
            opacity: 0.4, 
            textAlign: 'center' 
          }}
        >
          © Eventi 2025
        </CustomTypography>
      </Box>
    </Box>
  );
};

export default Login;
